// GrabCAD integration: the ExportBuilder plugin, the GrabCAD Print host
// registration, and the two elements only this plugin uses.
import type { FileExportArray, ExportStatusDetail } from '../../../../data/data-interfaces.ts';
import { ExportBuilder, dispatchExportStatus, isExportCancelled } from '../exportBuilder.ts';
import type { GrabCADExport, ExportOptions } from './GrabCADTypes.ts';
import {
  validateForGrabCAD, buildGrabCADExport, grabCadTX, type GrabCadOptions,
  grabCadImportFile, grabCadConvertFiles, grabCadDroppedFilePath, grabCadSaveFile,
  grabCadGetUserState, grabCadSetUserState, grabCadGetHost,
  type GrabCadImportOptions, type GrabCadStlUnit,
} from './grabcad.ts';
import { registerEnvironmentEnv, registerOrgIdEnv } from '../../../../data/envRegistry.ts';
import { type FileImportContext } from '../../import/fileImportProvider.ts';
import { registerHost, requestClose } from '../../../host/host.ts';
import { asApp } from '../../../../util/singletons.ts';
import { sendEv, isRealProjectSave } from '../../../../data/events.ts';
import { useShadow, useShadowStyle } from '../../../../util/wcUtil.ts';
import '../../../../components/text-and-lang/as-text.ts';
import '../../../../components/base/as-op-status.ts';
import '../../../../components/inputs/buttons/as-button.ts';
import type { AsOpStatus } from '../../../../components/base/as-op-status.ts';
import { AsButtonBase } from '../../../../components/inputs/buttons/as-button-base.ts';
import buttonStyleText from '../../../../components/inputs/buttons/as-button.css?raw';
import modalStyleText from './as-modal-grabcad-success.css?raw';


/**
 * <as-grabcad-export-button>
 *
 * Self-contained GrabCAD send button. Fires as-button-click synchronously on
 * click (giving parents a chance to configure the exporter), then calls
 * exporter.sendToGrabCAD(). Status is driven by as-export-status events emitted
 * by the grabcad plugin, displayed via an internal as-op-status.
 *
 * Attributes
 *   default-text   Button label
 *   dict-key       Localization key for label
 *   name           Passed in as-button-click detail
 *   package-name   Passed to exporter.sendToGrabCAD({ packageName })
 *   exporter-id    Which exporter to listen to (default: 'default')
 *   disabled       Disables the button
 *
 * Events
 *   as-button-click  bubbles, composed — fired synchronously before the async send
 */
export class AsGrabcadExportButton extends AsButtonBase {
  static readonly tag = 'as-grabcad-export-button' as const;

  static readonly observedAttributes = [
    'name', 'default-text', 'dict-key', 'pending-default-text', 'pending-dict-key',
    'package-name', 'exporter-id', 'disabled',
  ];

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(
      buttonStyleText + `
:host {
  align-items: center;
  gap: 0.25rem;
}
as-op-status { flex-shrink: 0; }`
    );
    return s;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, AsGrabcadExportButton._sheet];
    this._render();
  }

  connectedCallback(): void {
    this._attachListeners();
    window.addEventListener('as-export-status', this._onExportStatus);
    window.addEventListener('as-custom-data-changed', this._reset);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-export-status', this._onExportStatus);
    window.removeEventListener('as-custom-data-changed', this._reset);
  }

  attributeChangedCallback(_name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    this._render();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  reset(): void { this._reset(); }

  // ── Private ────────────────────────────────────────────────────────────────

  private _pending = false;

  private get _status(): AsOpStatus | null {
    return this.shadowRoot!.querySelector('as-op-status');
  }

  private _reset = (): void => {
    this._pending = false;
    this._updateButtonState();
    this._status?.reset();
  };

  private _onExportStatus = (e: Event): void => {
    const { exporterId, operation, state, message } = (e as CustomEvent<ExportStatusDetail>).detail;
    if (exporterId !== (this.getAttribute('exporter-id') ?? 'default')) return;
    if (operation !== 'grabcad') return;

    this._pending = state === 'pending';
    this._updateButtonState();

    switch (state) {
      case 'pending':   this._status?.setPending(); break;
      case 'success':   this._status?.setSuccess('Transfer Success'); break;
      case 'error':     this._status?.setError(message); break;
      // A cancel isn't a failure — drop straight back to idle.
      case 'cancelled': this._status?.reset(); break;
    }
  };

  /** Re-renders the button's inner content only — as-op-status is a sibling and is untouched. */
  private _updateButtonState(): void {
    const btn = this.shadowRoot?.querySelector('button');
    if (!btn) return;
    btn.disabled = this._pending || this.hasAttribute('disabled');

    const text    = this._pending ? (this.getAttribute('pending-default-text') ?? 'Sending...') : (this.getAttribute('default-text') ?? '');
    const dictKey = this._pending
      ? (this.getAttribute('pending-dict-key') ?? 'grabcad.transfer.sending')
      : this.getAttribute('dict-key');
    btn.innerHTML = dictKey
      ? `<as-text dict-key="${dictKey}" default-text="${text}"></as-text>`
      : `<as-text>${text}</as-text>`;
  }

  private _render(): void {
    const text     = this.getAttribute('default-text') ?? '';
    const dictKey  = this.getAttribute('dict-key');
    const disabled = this.hasAttribute('disabled');
    const textHtml = dictKey
      ? `<as-text dict-key="${dictKey}" default-text="${text}"></as-text>`
      : `<as-text>${text}</as-text>`;

    const statusTooltip = this.getAttribute('default-text') ?? 'Send to GrabCAD';
    this.shadowRoot!.innerHTML =
      `<button${disabled ? ' disabled' : ''}>${textHtml}</button><as-op-status tooltip-default-text="${statusTooltip}"></as-op-status>`;
    this._attachListeners();
  }

  private _attachListeners(): void {
    const btn = this.shadowRoot?.querySelector('button');
    if (!btn) return;
    btn.removeEventListener('click', this._handleClick);
    btn.addEventListener('click', this._handleClick);
  }

  private _handleClick = async (): Promise<void> => {
    // Fire synchronously — parent can configure the exporter in this handler
    // before the async send begins.
    this._dispatchButtonClick({ buttonName: this.getAttribute('name') });

    const exporter = (asApp() as any)?.exporter;
    if (!exporter) return;

    // Status updates arrive via as-export-status events emitted by the grabcad plugin.
    await exporter.sendToGrabCAD({ packageName: this.getAttribute('package-name') ?? undefined })
      .catch(() => { /* error state shown via as-export-status */ });
  };
}

if (!customElements.get(AsGrabcadExportButton.tag)) {
  customElements.define(AsGrabcadExportButton.tag, AsGrabcadExportButton);
} else {
  console.warn('as-grabcad-export-button already defined');
}


/** Safety net only. "Save and close" hands off to the built-in save path
 *  (grabCadSaveFile's own SAVE_FILE_TIMEOUT_MS, 2 min — see grabcad.ts), which
 *  reports a cancel via as-project-save-cancelled and is handled immediately
 *  by _onProjectSaveCancelled. This timer bounds the case where neither
 *  signal ever arrives. */
const SAVE_RECOVERY_MS = 5.5 * 60 * 1000;

export class AsModalGrabcadSuccess extends HTMLElement {
  static readonly tag = 'as-modal-grabcad-success' as const;

  private _saving = false;
  private _recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  private _onExportStatus = (e: Event): void => {
    const detail = (e as CustomEvent<ExportStatusDetail>).detail;
    if (detail.operation === 'grabcad' && detail.state === 'success') {
      this._setSaving(false);
      this._overlay()?.open(this.id);
    }
  };

  // 'as-after-project-saved' is dispatched on the as-app element (bubbles,
  // but not to descendants) by project-mixin.ts, so listen there rather than on
  // this element or window — this element is a descendant, and a
  // non-bubbling event dispatched on an ancestor never reaches it otherwise.
  // The autosave capture fires the same event, so check it's a real save
  // before dropping the spinner — otherwise an autosave tick landing mid-save
  // would tell the user their save is done while it's still running.
  private _onProjectSaved = (e: Event): void => { if (isRealProjectSave(e)) this._setSaving(false); };

  // Cancel counterpart: fires when the user dismisses the native GrabCAD save
  // dialog. Without this, cancelling left this modal's buttons stuck disabled
  // (and the save-and-close button spinning) until SAVE_RECOVERY_MS elapsed —
  // the built-in save path knows about the cancel immediately, this just
  // relays it instead of making the user wait out the blind recovery timer.
  private _onProjectSaveCancelled = (): void => this._setSaving(false);

  connectedCallback(): void {
    useShadow(this, /*html*/`
      <div class="modal-panel">
        <p class="message-main"><as-text dict-key="modals.grabcad.success.transferred">Your design was transferred to GrabCAD Print and was placed on the build tray.</as-text></p>
        <p class="message-secondary"><as-text dict-key="modals.grabcad.success.prepare">You can start the print preparation now after closing this app.</as-text></p>
        <p class="message-secondary"><as-text dict-key="modals.grabcad.success.saveFirst">Please don't forget to save your project beforehand.</as-text></p>
        <div class="actions">
          <as-button name="cancel" dict-key="modals.grabcad.success.cancel" default-text="Cancel"></as-button>
          <as-button name="close-app" dict-key="modals.grabcad.success.close" default-text="Close"></as-button>
          <as-button name="save-and-close" dict-key="modals.grabcad.success.saveAndClose" default-text="Save and close" pending-default-text="Saving…" pending-dict-key="modals.grabcad.success.saving"></as-button>
        </div>
      </div>
    `);
    useShadowStyle(this, modalStyleText, './as-modal-grabcad-success.css');

    this.shadowRoot!.addEventListener('as-button-click', (e: Event) => {
      if (this._saving) return;
      const name = (e as CustomEvent<{ buttonName: string | null }>).detail.buttonName;
      if (name === 'cancel') this._overlay()?.close();
      else if (name === 'close-app') sendEv(window, 'as-close-app');
      else if (name === 'save-and-close') {
        this._setSaving(true);
        sendEv(window, 'as-close-app', { detail: { save: true } });
      }
    });

    window.addEventListener('as-export-status', this._onExportStatus);
    asApp()?.addEventListener('as-after-project-saved', this._onProjectSaved);
    asApp()?.addEventListener('as-project-save-cancelled', this._onProjectSaveCancelled);
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-export-status', this._onExportStatus);
    asApp()?.removeEventListener('as-after-project-saved', this._onProjectSaved);
    asApp()?.removeEventListener('as-project-save-cancelled', this._onProjectSaveCancelled);
    this._setSaving(false);
  }

  /** Disables cancel/close-app and puts save-and-close in its pending
   *  (spinner) state while a save is in flight, so the click has visible
   *  feedback instead of looking like a no-op — and recovers on its own
   *  (see SAVE_RECOVERY_MS) if the save never reports back at all. */
  private _setSaving(saving: boolean): void {
    this._saving = saving;
    if (this._recoveryTimer) { clearTimeout(this._recoveryTimer); this._recoveryTimer = null; }

    const sr = this.shadowRoot;
    if (sr) {
      sr.querySelector('as-button[name="cancel"]')?.toggleAttribute('disabled', saving);
      sr.querySelector('as-button[name="close-app"]')?.toggleAttribute('disabled', saving);
      sr.querySelector('as-button[name="save-and-close"]')?.toggleAttribute('pending', saving);
    }

    if (saving) {
      this._recoveryTimer = setTimeout(() => this._setSaving(false), SAVE_RECOVERY_MS);
    }
  }

  private _overlay(): any {
    return this.closest('as-modal-overlay') ?? document.querySelector('#as-app-overlay');
  }
}

if (!customElements.get(AsModalGrabcadSuccess.tag)) {
  customElements.define(AsModalGrabcadSuccess.tag, AsModalGrabcadSuccess);
}


declare module '../exportBuilder.ts' {
  interface ExportBuilder {
    toGrabCAD(opts?: ExportOptions): Promise<GrabCADExport>;
    sendToGrabCAD(opts?: ExportOptions & GrabCadOptions): Promise<string>;
  }
}

/**
 * Plugin for ExportBuilder that adds GrabCAD export and transmission capabilities.
 */
export function grabcadPlugin(builder: ExportBuilder) {
  return {
    /**
     * Build and export to GrabCAD format.
     * For each auto-fetch entry: runs beforeExport() then fetches data.
     * All fetches run sequentially. Validates all mesh parts have fileType '3mf' before assembling.
     */
    async toGrabCAD(opts: ExportOptions = {}): Promise<GrabCADExport> {
      const resolvedFiles = await builder.resolve({ meshFileType: '3mf' });

      const fileArray = resolvedFiles as FileExportArray;
      const validation = validateForGrabCAD(fileArray);
      if (!validation.valid) {
        const lines = validation.errors.map((e) => `  - "${e.fileName ?? e.fileKey}": ${e.message}`);
        throw new Error(`[ExportBuilder] GrabCAD validation failed:\n${lines.join('\n')}`);
      }

      return buildGrabCADExport(fileArray, opts);
    },

    /**
     * Convenience wrapper: resolves to GrabCAD format then transmits in one call.
     * Returns the assembly ID from GrabCAD.
     */
    async sendToGrabCAD(opts: ExportOptions & GrabCadOptions = {}): Promise<string> {
      const id = builder.exporterId;
      dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'pending' });
      try {
        return await builder.runCancellable(async () => {
          const { target, targetOrigin, listenOn, ...exportOpts } = opts;
          const pkg = await this.toGrabCAD(exportOpts);
          if (pkg.parts.length === 0) {
            sendEv(asApp(), 'as-notify', {
              bubbles: false,
              detail: { defaultText: 'No parts to export. Check that your export scope is configured correctly.', dictKey: 'notifications.export.noParts', type: 'error', persist: true }
            });
            throw new Error('No mesh parts to export');
          }
          // Everything above is still recallable. Hold here until the export has
          // been cancellable for the full grace period — a small assembly can
          // resolve in well under a second, and without the wait the transfer
          // would be gone before the Cancel button could be reached.
          await builder.awaitCancelGrace();
          const assemblyId = await grabCadTX(pkg, {
            target, targetOrigin, listenOn,
            // Checked again at the last possible instant, immediately before the
            // postMessage — grabCadTX does real work first (transfer list, and
            // outside production a diagnostic that unzips every 3MF), and a
            // cancel arriving during that must still stop the send. The commit
            // is announced here and not a line earlier, because this is the
            // first moment it is actually true.
            onBeforeSend: () => {
              builder.throwIfCancelled();
              builder.markUncancellable();
            },
          });
          dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'success', message: `id: ${assemblyId}` });
          // Unified data-export log: a GrabCAD transfer is a data_export with
          // subcategory 'grabcad' (see as-app's as-data-export listener). No separate
          // started event — we only log on a completed (or failed) send.
          sendEv(asApp(), 'as-data-export', {
            detail: { subcategory: 'grabcad', assemblyId, fileInfo: pkg.parts.map((p) => ({ sizeBytes: p.data.byteLength, fileType: p.extension })) }
          });
          return assemblyId;
        });
      } catch (err: any) {
        if (isExportCancelled(err)) {
          // Nothing reached GrabCAD, so no data_export log and no error state.
          dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'cancelled' });
          throw err;
        }
        dispatchExportStatus({ exporterId: id, operation: 'grabcad', state: 'error', message: err.message });
        // Fold the failure into the same data_export log via detail.error.
        sendEv(asApp(), 'as-data-export', { detail: { subcategory: 'grabcad', error: err.message } });
        throw err;
      }
    }
  };
}

ExportBuilder.registerPlugin(grabcadPlugin);

ExportBuilder.registerButton({
  tagName: 'as-grabcad-export-button',
  requiredEnv: 'grabcad',
  panelPrefix: 'grabcad',
  extraAttrs: ['package-name'],
  defaultAttrs: {
    'default-text': 'Transfer to GrabCAD Print',
    'dict-key': 'grabcad.transfer.button',
    'tooltip-default-text': 'Transfer your design to GrabCAD Print. Models will be placed on the build tray, where you can prepare them for printing. Note: Depending on your selection, you can switch between the assembly and individual parts in the GrabCAD Print model tree.',
    'tooltip-dict-key': 'grabcad.transfer.tooltip',
  },
});

ExportBuilder.registerModal({ tagName: 'as-modal-grabcad-success', id: 'grabcad-success', disableBgClose: true });

function importOpts(ctx: FileImportContext): GrabCadImportOptions {
  return {
    unit: (ctx.getAttribute('grabcad-stl-unit') ?? 'mm') as GrabCadStlUnit,
    multifile: ctx.getAttribute('multiple') !== null,
    onProgress: ctx.onProgress,
    onImportProgress: ctx.onImportProgress,
    signal: ctx.signal,
  };
}

// Prefs live in GrabCAD's user state as one JSON blob under a single key, so
// hydration is a single round-trip however many prefs there are. That matters:
// it happens on every launch, and something is usually waiting on it.
const PREFS_STATE_KEY = 'as-prefs';

// Everything the GrabCAD Print host provides, in one registration. Core code
// reads it through features/host/host.ts and never imports anything from here.
registerHost('grabcad', {
  // GrabCAD Print answers a getHost message with its name.
  checkHost: async () => (await grabCadGetHost())?.toLowerCase() === 'grabcad',

  // GrabCAD Print runs the app in an Electron webview where window.close() is a
  // no-op. The host exposes `onQuit(close)` for the real quit, dispatches a
  // cancelable `closeRequested` when its close button is pressed (bridged to
  // `as-close-requested` below), and treats a truthy beforeunload returnValue as
  // "stay" without a dialog of its own.
  window: {
    quit: (close) => {
      const onQuit = (window as any).onQuit;
      typeof onQuit === 'function' ? onQuit(close) : close();
    },
    vetoesUnload: true,
  },

  // saveFileAs() routes here, replacing the browser <a download> with GrabCAD's
  // native save dialog. FileSaveOptions (title/fileExtension/fileExtensionName)
  // map 1:1 onto the saveFile message fields.
  fileSave: {
    saveStatusTrackable: true,
    saveFile: (blob, filename, opts) => grabCadSaveFile(blob, filename, opts),
  },

  // <as-file-input>: GrabCAD's native open/convert flow replaces the standard
  // file input.
  //   Browse: open-file dialog + convert (grabCadImportFile; the dialog allows
  //           multi-select when the component has the `multiple` attribute).
  //   Drop:   the OS path arrives on the dropped File, so the dialog step is
  //           skipped and each path goes straight to convert (sequentially —
  //           the protocol correlates one conversion at a time). Blobs without
  //           an OS path (internal transfers, synthetic blobs) are declined and
  //           take the normal drop flow.
  // kinds: ['mesh'] — GrabCAD's open dialog and convert pipeline only handle
  // CAD/mesh files. Image/text/data uploads (category != 'mesh') bypass this
  // provider and use the browser's native file input instead.
  fileImport: {
    kinds: ['mesh'],
    // Conversion honours ctx.signal — best effort, since GrabCAD may or may not
    // act on abortConvert (see grabCadConvertAndOpenFile's abort()). Either way
    // the user stops waiting, so the Cancel action is worth offering.
    cancellable: true,
    pickFile: (ctx) => grabCadImportFile(importOpts(ctx)),
    claimDrop: (blobs, ctx) => {
      const paths = blobs.map(grabCadDroppedFilePath).filter((p): p is string => !!p);
      return paths.length ? () => grabCadConvertFiles(paths, importOpts(ctx)) : null;
    },
    // GrabCAD Print's own open/convert dialog takes far more than STL — link to
    // Stratasys's own list rather than duplicating (and drifting from) it here.
    acceptHintText: 'Supported formats:',
    acceptHintUrl: 'https://support.stratasys.com/en/Software/GrabCAD-Print/About/List-of-supported-file-formats',
  },

  // GrabCAD Print clears IndexedDB between launches, so prefs wouldn't survive
  // a relaunch on their own. They're mirrored into GrabCAD's own user state and
  // pulled back at startup — see db.ts's prefs section.
  prefs: {
    async loadAll() {
      const raw = await grabCadGetUserState(PREFS_STATE_KEY);
      // Null covers both "never set" and "host didn't answer" — nothing to adopt
      // either way.
      if (!raw) return null;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        console.warn('[GrabCAD prefs] stored value is not valid JSON — ignoring');
        return null;
      }
      // Anything but a plain object means something else is using the key. Better
      // to ignore it than to feed garbage into the local store.
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        console.warn('[GrabCAD prefs] stored value is not an object — ignoring');
        return null;
      }
      return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)]));
    },
    saveAll: (prefs) => grabCadSetUserState(PREFS_STATE_KEY, JSON.stringify(prefs)),
  },

  // GrabCAD Print opens the destination app in its own embedded webview; a
  // browser "open in new tab" makes no sense there, so suppress that
  // transfer-modal option. Language is GrabCAD's own to control — it launches
  // the app with a `lang` URL param (picked up generically by as-app, see
  // _initLang), so the in-app selector is disabled rather than offering a switch
  // that wouldn't stick. The user-options icon (account/sign-out) has nothing to
  // show either, since GrabCAD handles its own auth outside app-suite.
  settings: { allowTransferNewTab: false, allowLangSelector: false, showUserOptions: false, externalLinksViaLightDomAnchor: true },
});

if (typeof window !== 'undefined') {
  window.addEventListener('closeRequested', (e) => { if (requestClose()) e.preventDefault(); });
  // Manual check of the getHost message from the console: `await testHost()`.
  (window as any).testHost = async () => {
    const host = await grabCadGetHost();
    console.log('[GrabCAD] getHost answered:', host ?? '(no answer)');
    return host;
  };
}

registerOrgIdEnv(-1, 'grabcad');
registerEnvironmentEnv('grabcad', 'grabcad');
